import { NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { Global } from '@iupics-manager/models/global-var';

/**
 * Runs fn once the requests in progress are done (at most maxWaitMs later), in Angular: secondary work (badges,
 * preview, recent items) no longer queues before what the screen waits for (the record, its child tabs). Checks
 * every 100 ms outside Angular. Returns a cancel function.
 */
export function whenRequestsSettle(zone: NgZone, fn: () => void, maxWaitMs = 1500): () => void {
  const start = Date.now();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let cancelled = false;
  const check = () => {
    if (cancelled) return;
    if (Global.activeXHRRequest <= 0 || Date.now() - start >= maxWaitMs) {
      zone.run(fn);
      return;
    }
    timer = setTimeout(check, 100);
  };
  // first check after the current task: the requests started by the caller are counted by then
  zone.runOutsideAngular(() => (timer = setTimeout(check, 0)));
  return () => {
    cancelled = true;
    clearTimeout(timer);
  };
}

/** Emits once the requests in progress are done (see whenRequestsSettle); unsubscribing cancels the wait. */
export function requestsSettled$(zone: NgZone, maxWaitMs = 1500): Observable<void> {
  return new Observable<void>((subscriber) =>
    whenRequestsSettle(
      zone,
      () => {
        subscriber.next();
        subscriber.complete();
      },
      maxWaitMs
    )
  );
}
